import React from 'react';
import LoadingSpinner from './LoadingSpinner';
import EmptyState from './EmptyState';

/**
 * columns: [{ key, header, render?(row), align?, width?, className? }]
 * rows:    [object]
 * loading: boolean
 * empty:   ReactNode | string (replaces default empty state)
 * onRowClick: (row) => void
 * keyField:   string  (defaults to 'id')
 */
export default function DataTable({
  columns,
  rows,
  loading,
  error,
  empty,
  onRowClick,
  keyField = 'id',
  className = '',
  stickyHeader = true,
  density = 'comfortable',
}) {
  const rowPad = density === 'compact' ? 'px-4 py-2' : 'px-4 py-3';

  if (loading) return <LoadingSpinner label="Loading..." />;

  return (
    <div className={`overflow-hidden rounded-lg border border-slate-200 bg-white shadow-card ${className}`}>
      <div className="max-h-[calc(100vh-260px)] overflow-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead
            className={`${stickyHeader ? 'sticky top-0 z-10' : ''} bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-ink-muted`}
          >
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`${rowPad} ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : ''} ${c.className || ''}`}
                  style={c.width ? { width: c.width } : undefined}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {error ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-10 text-center text-sm text-danger"
                >
                  Failed to load data
                </td>
              </tr>
            ) : !rows || rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-2">
                  {typeof empty === 'string' || !empty ? (
                    <EmptyState
                      title={empty || 'No records found'}
                      description="Try adjusting filters or create a new entry."
                    />
                  ) : (
                    empty
                  )}
                </td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr
                  key={row[keyField] ?? i}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={
                    onRowClick
                      ? 'cursor-pointer transition-colors hover:bg-slate-50'
                      : ''
                  }
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={`${rowPad} text-ink ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : ''} ${c.cellClassName || ''}`}
                    >
                      {c.render ? c.render(row) : row[c.key] ?? <span className="text-ink-faint">—</span>}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
